// Application: Actions for sending in an application.
import { Dispatch } from 'redux';

import { get, post, RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import customHistory from '../customHistory';
import { ResponseWithError } from '../generated-types';
import { AppState } from '../reducers';
import { customFailed } from '../util/common';
import ApplicationStatus from '../util/mappings/applicationStatus';
import { errorNames } from '../util/mappings/errorName';
import LoanType from '../util/mappings/loanType';
import RoutePath from '../util/mappings/routePath';
import { trackEvent, TrackingStep } from '../util/piwik-utils';
import { fetchConsent, saveConsent } from './consent';
import { openStep } from './steps/steps';
import { getRequestHeaders } from './tools';
import { actionTypes, AppActions } from './types';

type Action<T> = import('./types').Action<T>;
type FetchApplicationResponse = import('../generated-types').LoanApplicationResponse;
type SendApplicationResponse = import('../generated-types').LoanApplicationResponse;
type UpdateApplicationResponse = import('../generated-types').LoanApplicationResponse;

export interface FetchApplicationStartedAction extends Action<typeof actionTypes.FETCH_APPLICATION_STARTED> {
    type: typeof actionTypes.FETCH_APPLICATION_STARTED;
}

export interface FetchApplicationFulfilledAction extends Action<typeof actionTypes.FETCH_APPLICATION_FULFILLED> {
    type: typeof actionTypes.FETCH_APPLICATION_FULFILLED;
    payload: FetchApplicationResponse;
}

export interface FetchApplicationFailedAction extends Action<typeof actionTypes.FETCH_APPLICATION_FAILED> {
    type: typeof actionTypes.FETCH_APPLICATION_FAILED;
    payload: RequestExceptionWithResponse;
}

export interface SendApplicationStartedAction extends Action<typeof actionTypes.SEND_APPLICATION_STARTED> {
    type: typeof actionTypes.SEND_APPLICATION_STARTED;
}

export interface SendApplicationFulfilledAction extends Action<typeof actionTypes.SEND_APPLICATION_FULFILLED> {
    type: typeof actionTypes.SEND_APPLICATION_FULFILLED;
    payload?: SendApplicationResponse;
}

export interface SendApplicationFailedAction extends Action<typeof actionTypes.SEND_APPLICATION_FAILED> {
    type: typeof actionTypes.SEND_APPLICATION_FAILED;
    payload: RequestExceptionWithResponse;
}

export interface UpdateApplicationStartedAction extends Action<typeof actionTypes.UPDATE_APPLICATION_STARTED> {
    type: typeof actionTypes.UPDATE_APPLICATION_STARTED;
}

export interface UpdateApplicationFulfilledAction extends Action<typeof actionTypes.UPDATE_APPLICATION_FULFILLED> {
    type: typeof actionTypes.UPDATE_APPLICATION_FULFILLED;
    payload: UpdateApplicationResponse;
}

export interface UpdateApplicationFailedAction extends Action<typeof actionTypes.UPDATE_APPLICATION_FAILED> {
    type: typeof actionTypes.UPDATE_APPLICATION_FAILED;
    payload: RequestExceptionWithResponse;
}

export type ApplicationActions =
    | FetchApplicationStartedAction
    | FetchApplicationFulfilledAction
    | FetchApplicationFailedAction
    | SendApplicationStartedAction
    | SendApplicationFulfilledAction
    | SendApplicationFailedAction
    | UpdateApplicationStartedAction
    | UpdateApplicationFulfilledAction
    | UpdateApplicationFailedAction;

export const fetchApplicationStarted = (): FetchApplicationStartedAction => ({
    type: actionTypes.FETCH_APPLICATION_STARTED,
});
export const fetchApplicationFulfilled = (json: FetchApplicationResponse): FetchApplicationFulfilledAction => ({
    type: actionTypes.FETCH_APPLICATION_FULFILLED,
    payload: json,
});
export const fetchApplicationFailed = (error: RequestExceptionWithResponse): FetchApplicationFailedAction => ({
    type: actionTypes.FETCH_APPLICATION_FAILED,
    payload: error,
});

export const sendApplicationStarted = (): SendApplicationStartedAction => ({
    type: actionTypes.SEND_APPLICATION_STARTED,
});
export const sendApplicationFulfilled = (json?: SendApplicationResponse): SendApplicationFulfilledAction => ({
    type: actionTypes.SEND_APPLICATION_FULFILLED,
    payload: json,
});
export const sendApplicationFailed = (error: RequestExceptionWithResponse): SendApplicationFailedAction => ({
    type: actionTypes.SEND_APPLICATION_FAILED,
    payload: error,
});

export const updateApplicationStarted = (): UpdateApplicationStartedAction => ({
    type: actionTypes.UPDATE_APPLICATION_STARTED,
});
export const updateApplicationFulfilled = (json: UpdateApplicationResponse): UpdateApplicationFulfilledAction => ({
    type: actionTypes.UPDATE_APPLICATION_FULFILLED,
    payload: json,
});
export const updateApplicationFailed = (error: RequestExceptionWithResponse): UpdateApplicationFailedAction => ({
    type: actionTypes.UPDATE_APPLICATION_FAILED,
    payload: error,
});

export const fetchApplication =
    (options?: { skipConsent?: boolean }) =>
    async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
        const { loader, context } = getState();
        const { isFetchingApplication } = loader;

        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const idParam = urlParams.get('id');
        const applicationId = idParam ? idParam.replace(/\/$/, '') : null;
        if (applicationId && !isFetchingApplication) {
            try {
                dispatch(fetchApplicationStarted());
                const response: FetchApplicationResponse = await get(
                    `${actionTypes.RESOURCE_PATH}/application/ongoing/${applicationId}`,
                    getRequestHeaders(context.config.bankregnr),
                );
                if (
                    (response.applicationStatus === ApplicationStatus.newCase ||
                        response.applicationStatus === ApplicationStatus.documentsSent) &&
                    !response.applicantMissingData &&
                    response.otherApplicantMissingData
                ) {
                    dispatch(fetchApplicationFailed(customFailed(errorNames.otherApplicantMissingData)));
                } else {
                    if (!options?.skipConsent) {
                        await fetchConsent(dispatch, getState);
                    }
                    dispatch(fetchApplicationFulfilled(response));
                    // Redirect to Receipt if Control or Rejected
                    if (
                        (response.applicationStatus === ApplicationStatus.approved &&
                            response.loanInfo.loanType === LoanType.loanCommitment) ||
                        response.applicationStatus === ApplicationStatus.control ||
                        response.applicationStatus === ApplicationStatus.rejected
                    ) {
                        customHistory.push(RoutePath.applicationReceipt);
                    }
                }
            } catch (error) {
                dispatch(fetchApplicationFailed(error));
            }
        }
    };

export const fetchApplicationById =
    (applicationId: string) =>
    async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
        const { loader, context } = getState();
        const { isFetchingApplication } = loader;

        if (applicationId && !isFetchingApplication) {
            try {
                dispatch(fetchApplicationStarted());
                const response: FetchApplicationResponse = await get(
                    `${actionTypes.RESOURCE_PATH}/application/ongoing/${applicationId}`,
                    getRequestHeaders(context.config.bankregnr),
                );
                if (response.loanInfo.loanAmountWithoutFees === undefined) {
                    dispatch(fetchApplicationFailed(customFailed(errorNames.internalError)));
                    return;
                }
                dispatch(fetchApplicationFulfilled(response));
            } catch (error) {
                dispatch(fetchApplicationFailed(error));
            }
        }
    };

export const sendApplication = async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
    const { application, calculator, loader, context, kyc } = getState();
    const { mainApplicant, hasCoApplicant, coApplicant } = application;
    const { isSendingApplication, hasSentApplication } = loader;
    const { completionPathSecuredLoan } = context.config.data;
    const { kycAnswered } = kyc;
    const {
        loanType,
        vehiclePrice,
        loanProduct,
        equity,
        requestedLoanPeriodInMonths,
        vehicleRegistrationNumber,
        odometerValue,
    } = calculator.loanInfo;

    const data = {
        mainApplicant,
        coApplicant: hasCoApplicant ? coApplicant : null,
        loanInfo: {
            loanType,
            vehiclePrice,
            loanProduct,
            equity,
            requestedLoanPeriodInMonths,
            vehicleRegistrationNumber: vehicleRegistrationNumber || null,
            odometerValue: odometerValue !== null ? odometerValue : null,
        },
        kycAnswered,
        isEikaDigitalBank: context.config.isEikaDigitalBank,
    };

    if (!isSendingApplication && !hasSentApplication) {
        try {
            dispatch(sendApplicationStarted());
            const response: ResponseWithError<SendApplicationResponse> = await post(
                `${actionTypes.RESOURCE_PATH}/application/submit`,
                data,
                getRequestHeaders(context.config.bankregnr),
            );
            await saveConsent(dispatch, getState);
            if (response.success) {
                switch (response.responseOrNull?.applicationStatus) {
                    case ApplicationStatus.approved:
                    case ApplicationStatus.documentsSent:
                    case ApplicationStatus.documentsComplete:
                        trackEvent(TrackingStep.BillanSoknadGodkjent);
                        break;
                    case ApplicationStatus.control:
                        trackEvent(TrackingStep.BillanSoknadManuellKontroll);
                        break;
                    case ApplicationStatus.rejected:
                        trackEvent(TrackingStep.BillanSoknadAvslag);
                        break;
                    default:
                        break;
                }
                switch (response.responseOrNull?.profitability) {
                    case '3':
                        trackEvent(TrackingStep.BillanSoknadProfitabilityHigh);
                        break;
                    case '2':
                        trackEvent(TrackingStep.BillanSoknadProfitabilityMedium);
                        break;
                    case '1':
                        trackEvent(TrackingStep.BillanSoknadProfitabilityLow);
                        break;
                    case '0':
                        trackEvent(TrackingStep.BillanSoknadProfitabilityMissing);
                        break;
                    default:
                        break;
                }
                dispatch(sendApplicationFulfilled(response.responseOrNull || undefined));
            }
            window.location.href = `${completionPathSecuredLoan}?id=${response.responseOrNull?.applicationId}`;
            if (response.failure && !response.success) {
                dispatch(sendApplicationFailed(customFailed(response.errorOrNull || '')));
            }
        } catch (error) {
            dispatch(sendApplicationFailed(error));
        }
    }
};

export const updateApplication = async (dispatch: Dispatch<AppActions>, getState: () => AppState): Promise<void> => {
    const { loader, context, kyc, completion, sbl } = getState();
    const { applicationId, email, phone, orderInsurance } = completion.ongoing;
    const { isUpdatingApplication, hasUpdatedApplication } = loader;
    const { kycAnswered } = kyc;

    const data = {
        applicationId,
        email,
        phone,
        kycAnswered,
        orderInsurance,
    };

    if (!isUpdatingApplication && !hasUpdatedApplication) {
        await saveConsent(dispatch, getState);
        try {
            dispatch(updateApplicationStarted());
            const response: UpdateApplicationResponse = await post(
                `${actionTypes.RESOURCE_PATH}/application/update`,
                data,
                getRequestHeaders(context.config.bankregnr),
            );
            const { applicationStatus, loanInfo, resourceError } = response;

            if (resourceError === errorNames.sblSaveError) {
                if (sbl.sblStatus === 'ACCEPTED') {
                    dispatch(fetchApplicationFailed(customFailed(errorNames.sblSaveError)));
                } else {
                    // Missing SBL consent in cache, go to step 1
                    dispatch(openStep(1));
                }
            } else if (
                (applicationStatus === ApplicationStatus.newCase ||
                    applicationStatus === ApplicationStatus.documentsSent) &&
                !response.applicantMissingData &&
                response.otherApplicantMissingData
            ) {
                dispatch(fetchApplicationFailed(customFailed(errorNames.otherApplicantMissingData)));
            } else if (
                applicationStatus === ApplicationStatus.newCase &&
                !response.applicantMissingData &&
                !response.otherApplicantMissingData
            ) {
                dispatch(fetchApplicationFailed(customFailed(errorNames.incorrectNewCase)));
            } else if (
                (applicationStatus === ApplicationStatus.approved && loanInfo.loanType === LoanType.loanCommitment) ||
                (applicationStatus === ApplicationStatus.documentsSent &&
                    loanInfo.loanType === LoanType.loanCommitment &&
                    !response.applicantMissingData &&
                    !response.otherApplicantMissingData) ||
                applicationStatus === ApplicationStatus.control ||
                applicationStatus === ApplicationStatus.rejected
            ) {
                customHistory.push(RoutePath.applicationReceipt);
            } else {
                dispatch(updateApplicationFulfilled(response));
            }
        } catch (error) {
            dispatch(updateApplicationFailed(error));
        }
    }
};

export const insuranceClickInc = () => {
    get(`${actionTypes.RESOURCE_PATH}/application/insurance-click`);
};
